import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Star, Truck } from 'lucide-react';
import { CONTACT_INFO, HERO_DATA } from '../data/mockData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0A0A0C] border-t border-white/5">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div>
            <img 
              src="/logo.png" 
              alt="Detailing Disciples" 
              className="h-32 w-auto object-contain mb-4"
            />
            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
              Owner-operated premium auto detailing, paint correction, ceramic coating, and interior care based in Simi Valley. Mobile service available across Ventura County and select Los Angeles communities.
            </p>
            <div className="mb-4 text-sm">
              {[...Array(5)].map((_, i) => (
                <Star key={`footer-star-${i}`} className="w-4 h-4 fill-[#B8860B] text-[#B8860B] inline" />
              ))}
              <span className="text-gray-400 ml-2">{HERO_DATA.rating} {HERO_DATA.reviewSources}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/services" className="text-gray-400 hover:text-[#B8860B] transition-colors text-sm">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/results" className="text-gray-400 hover:text-[#B8860B] transition-colors text-sm">
                  Results
                </Link>
              </li>
              <li>
                <Link to="/method" className="text-gray-400 hover:text-[#B8860B] transition-colors text-sm">
                  The Method
                </Link>
              </li>
              <li>
                <Link to="/reviews" className="text-gray-400 hover:text-[#B8860B] transition-colors text-sm">
                  Reviews
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-400 hover:text-[#B8860B] transition-colors text-sm">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-[#B8860B] transition-colors text-sm">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-[#B8860B] hover:text-[#C8960B] transition-colors text-sm font-medium">
                  Request a Quote
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-sm">
              <li className="text-gray-400">Basic Hand Wash</li>
              <li className="text-gray-400">Mini Detail</li>
              <li className="text-gray-400">Premium Detail</li>
              <li className="text-gray-400">Paint Correction</li>
              <li className="text-gray-400">Ceramic Coating</li>
            </ul>
            <div className="mt-6">
              <h4 className="text-white font-semibold mb-3 text-sm">Service Areas</h4>
              <div className="flex flex-wrap gap-2">
                {CONTACT_INFO.serviceAreas.slice(0, 4).map((area, index) => (
                  <span key={area} className="text-xs text-gray-400">
                    {area}{index < 3 ? ',' : ''}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">+ more areas</p>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <div className="space-y-3">
              <a
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center space-x-3 text-gray-400 hover:text-[#B8860B] transition-colors text-sm group"
              >
                <Phone className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span>{CONTACT_INFO.phone}</span>
              </a>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center space-x-3 text-gray-400 hover:text-[#B8860B] transition-colors text-sm group"
              >
                <Mail className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span>{CONTACT_INFO.email}</span>
              </a>
              <div className="flex items-center space-x-3 text-gray-400 text-sm">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <div>
                  <span>{CONTACT_INFO.address}</span>
                  <span className="block text-[#B8860B] text-xs mt-1">{CONTACT_INFO.addressNote}</span>
                </div>
              </div>
              <div className="flex items-center space-x-3 text-gray-400 text-sm">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-[#B8860B]">{CONTACT_INFO.mobileService}</span>
              </div>
              <div className="flex items-start space-x-3 text-gray-400 text-sm bg-[#B8860B]/5 border border-[#B8860B]/20 rounded-lg p-3 -ml-3">
                <Truck className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#B8860B]" />
                <div>
                  <span className="text-white font-semibold block">Detailing Garage Available</span>
                  <span className="text-[#B8860B] text-xs">Appointments Only • Simi Valley</span>
                </div>
              </div>
              <div className="flex items-start space-x-3 text-gray-400 text-sm">
                <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <div>Mon-Fri: {CONTACT_INFO.hours.weekday}</div>
                  <div>Sat-Sun: {CONTACT_INFO.hours.weekend}</div>
                </div>
              </div>
            </div>
            
            {/* Social Media */}
            <div className="mt-6">
              <h4 className="text-white font-semibold mb-3 text-sm">Follow Us</h4>
              <div className="flex items-center space-x-3">
                <a
                  href={CONTACT_INFO.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#B8860B]/20 flex items-center justify-center text-gray-400 hover:text-[#B8860B] transition-all group"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </a>
                <a
                  href={CONTACT_INFO.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#B8860B]/20 flex items-center justify-center text-gray-400 hover:text-[#B8860B] transition-all group"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </a>
                <a
                  href={CONTACT_INFO.socialMedia.yelp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#B8860B]/20 flex items-center justify-center text-gray-400 hover:text-[#B8860B] transition-all group"
                  aria-label="Yelp"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                </a>
                <a
                  href={CONTACT_INFO.socialMedia.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#B8860B]/20 flex items-center justify-center text-gray-400 hover:text-[#B8860B] transition-all group"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © {currentYear} Detailing Disciples. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link to="/privacy" className="text-gray-500 hover:text-[#B8860B] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-[#B8860B] transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
